import { useEffect, useState } from 'react';
import { carService } from 'shared/services/carService';
import { ICarFormsProps, ICarVarianItem, ICarDataItem, ICarSingleVariant, ITalentCars } from 'shared/interfaces/ICars';
import useInfiniteScroll from 'shared/hooks/useInifiniteScrollAutoComplete';

const { getManufacturer, getModel, getVariant, getSingleVariant } = carService();

const INITIAL_PAGE = 1;
const INITIAL_LIMIT = 50;

type PageProps = {
  make: number;
  model: number;
  variant: number;
};

const usePaginationScroll = (hasNextPage: boolean, onPageChange: () => void) => {
  const { handleScroll } = useInfiniteScroll({
    hasNextPage,
    onPageChange,
  });
  return handleScroll;
};

const useUpdateData = <T>(
  loading: boolean,
  data: T[] | undefined,
  setData: React.Dispatch<React.SetStateAction<T[]>>,
) => {
  useEffect(() => {
    if (!loading && data) {
      setData((prev) => [...prev, ...data]);
    }
  }, [loading, data, setData]);
};
const sortItems = (items: ICarDataItem[]) => items?.sort((a, b) => a?.name?.localeCompare(b?.name)) || [];

export const useCarForm = (data?: ITalentCars) => {
  // Page Configurations and Functions
  const [page, setPage] = useState<PageProps>({
    make: INITIAL_PAGE,
    model: INITIAL_PAGE,
    variant: INITIAL_PAGE,
  });

  // On Page Change
  const handlePageChange = (key: 'make' | 'model' | 'variant', reset?: boolean) => {
    setPage((prev) => {
      return { ...prev, [key]: reset ? 1 : prev[key] + 1 };
    });
  };

  // Data
  const [makeData, setMakeData] = useState<ICarDataItem[]>([]);
  const [modelData, setModelsData] = useState<ICarDataItem[]>([]);
  const [variantData, setVariantData] = useState<ICarVarianItem[]>([]);

  // State variables for selected values and inputs
  const [selectedMake, setSelectedMake] = useState<ICarDataItem | null>(data?.manufacturer || null);
  const [selectedModel, setSelectedModel] = useState<ICarDataItem | null>(data?.model || null);
  const [selectedVariant, setSelectedVariant] = useState<ICarVarianItem | null>(data?.variant || null);
  const [selectedSingleVariant, setSelectedSingleVariant] = useState<ICarSingleVariant | null>(
    data?.singleVariant || null,
  );

  // Fetch manufacturers
  const { data: manufactureData, isLoading: manufacturerLoading } = getManufacturer({
    page: page.make,
    limit: 100,
  });

  // Fetch Models
  const { data: modelsData, isLoading: modelLoading } = getModel({
    manufacturerId: selectedMake?.id || 0,
    page: page.model,
    limit: INITIAL_LIMIT,
  });

  // Fetch Variant
  const { data: variantDatas, isLoading: variantLoading } = getVariant({
    manufacturerId: selectedMake?.id || 0,
    modelName: selectedModel?.name || '',
    page: page.variant,
    limit: INITIAL_LIMIT,
  });

  // Fetch Single Variant
  const { data: singleVariantData, isLoading: singleVariantLoading } = getSingleVariant({
    variantId: selectedVariant?.id || 0,
  });

  // Paginatiion Scroll
  const handleScrollMake = usePaginationScroll(Boolean(manufactureData?.hasNextPage), () => handlePageChange('make'));
  const handleScrollModel = usePaginationScroll(Boolean(modelsData?.hasNextPage), () => handlePageChange('model'));
  const handleScrollVariant = usePaginationScroll(Boolean(variantDatas?.hasNextPage), () =>
    handlePageChange('variant'),
  );

  // Handle changes in manufacturer selection
  const handleMakeChange = (manufacturer: ICarDataItem | null) => {
    if (manufacturer?.name === selectedMake?.name) return;
    setSelectedMake(manufacturer || null);

    setSelectedSingleVariant(null);

    handlePageChange('model', true);
    handlePageChange('variant', true);

    setModelsData([]);
    setVariantData([]);
    setSelectedModel(null);
    setSelectedVariant(null);
    setSelectedSingleVariant(null);
  };

  // Handle changes in model selection
  const handleModelChange = (model: ICarDataItem | null) => {
    if (model?.name === selectedModel?.name) return;
    setSelectedModel(model || null);
    setSelectedSingleVariant(null);

    handlePageChange('variant', true);
    setVariantData([]);
    setSelectedVariant(null);
  };

  // Handle changes in variant selection
  const handleVariantChange = (variant: ICarVarianItem | null) => {
    if (variant?.name === selectedVariant?.name) return;
    setSelectedVariant(variant);
    setSelectedSingleVariant(null);
  };

  useEffect(() => {
    if (data) {
      setSelectedMake(data.manufacturer || null);
      setSelectedModel(data.model || null);
      setSelectedVariant(data.variant || null);
      setSelectedSingleVariant(data.singleVariant || null);
    }
  }, [data]);

  // Updating Data
  useUpdateData(manufacturerLoading, manufactureData?.data, setMakeData);
  useUpdateData(modelLoading, modelsData?.data, setModelsData);
  useUpdateData(variantLoading, variantDatas?.data, setVariantData);
  useEffect(() => {
    if (!singleVariantLoading && singleVariantData) {
      setSelectedSingleVariant({
        type: singleVariantData?.make_model_trim_body?.type,
      });
    }
  }, [singleVariantLoading, singleVariantData]);

  // Form configuration
  const carForms: ICarFormsProps[] = [
    {
      name: 'Manufacturer',
      list: sortItems(makeData) || [],
      isLoading: manufacturerLoading,
      value: selectedMake || null,
      placeholder: selectedMake?.name || '',
      noOptionText: manufacturerLoading ? 'Fetching data...' : 'No Options',
      handleOnChange: handleMakeChange,
      onScroll: handleScrollMake,
    },
    {
      name: 'Model',
      list: sortItems(modelData) || [],
      isLoading: modelLoading,
      value: selectedModel || null,
      placeholder: selectedModel?.name || '',
      noOptionText: modelLoading ? 'Fetching data...' : 'No Options',
      disable: !selectedMake,
      handleOnChange: handleModelChange,
      onScroll: handleScrollModel,
    },
    {
      name: 'Variant',
      list: variantData?.sort((a, b) => a?.name?.localeCompare(b?.name)) || [],
      isLoading: variantLoading,
      value: selectedVariant || null,
      placeholder: selectedVariant?.name || '',
      noOptionText: variantLoading ? 'Fetching data...' : 'No Options',
      disable: !selectedModel || !selectedMake,
      handleOnChange: handleVariantChange,
      onScroll: handleScrollVariant,
    },
    {
      name: 'Vehicle Type',
      list: [],
      value: null,
      handleOnChange: () => {},
      readOnly: true,
      disable: !selectedMake || !selectedModel || !selectedVariant,
      placeholder: selectedSingleVariant?.type || 'Variant is required...',
      isStringForm: {
        value: selectedSingleVariant?.type || '',
      },
    },
  ];

  return { carForms, selectedMake, selectedModel, selectedVariant, selectedSingleVariant };
};
